import { stringify } from 'qs';
import Bugsnag from 'bugsnag-js';
import { curry, isEmpty } from 'lodash';

import { authenticatedFetch } from 'features/authentication';

const API_VERSION = 1;
const ACCEPT_HEADER = { Accept: `application/json; version=${API_VERSION}` };
const CONTENT_TYPE_HEADER = { 'Content-type': 'application/json' };

function makeApiUrl(endpoint, query) {
  const queryString = stringify(query);
  return `${endpoint}${queryString ? '?' : ''}${queryString}`;
}

/**
 * Check the status of an API response. If `response` is simply an object with an `error`
 * property (of type `Error`), this indicates something failed _before_ the fetch call
 * (for example, an attempt to call an authenticated endpoint without authentication).
 * Otherwise, inspect the response status to determine if the API request returned an error status.
 * @param {{error}|Response} response
 * @return {Response} if no errors are detected
 * @throws Will throw an error on any failure
 */
function checkStatus(response) {
  if (response.error && (response.error instanceof Error)) {
    // indicates a failure before even hitting the API - rethrow
    throw response.error;
  }

  if (response.status < 400) {
    // no errors, no worries
    return response;
  }

  // the response had a status code, so wrap it up in an Error that Bugsnag will handle...
  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

function processError({ endpoint, query, auth, url, method }) {
  const metadata = {
    endpoint,
    query,
    auth,
    url,
    method,
  };

  // const name = `API Error: ${method} ${endpoint}`;
  const severity = 'error';

  return (error) => {
    // console.error(`[FETCH] ${name}`, error);
    const message = error.message || `${error.response.status} error calling ${endpoint}`;
    Bugsnag.notifyException(error, message, metadata, severity);
    return ({ error });
  };
}

/**
 * Given a response, return a function that will accept the body
 * of that response and wrap it up with the headers and response
 * status into a JS object.
 * @param response
 * @returns {function}
 */
const makeResponseBuilder = response => body => ({
  response: {
    headers: response.headers,
    body,
    status: response.status,
  },
});

function processResponse(response) {
  const contentType = response.headers.get('content-type');
  const responseBuilder = makeResponseBuilder(response);
  if (contentType && contentType.includes('json')) {
    return response.json().then(responseBuilder);
  }

  return response.text().then(responseBuilder);
}

function buildFetchArgs({
  method, headers = {}, requestBody = {},
}) {
  const fetchOpts = {
    method,
    headers: {
      ...ACCEPT_HEADER,
      ...CONTENT_TYPE_HEADER,
      ...headers,
    },
  };
  return isEmpty(requestBody) ? fetchOpts : { ...fetchOpts, body: JSON.stringify(requestBody) };
}

function execute(method, endpoint, {
  query, auth = true, headers = {}, requestBody = {},
} = {}) {
  const url = makeApiUrl(endpoint, query);
  const options = buildFetchArgs({ method, headers, requestBody });
  const fetchFn = auth ? authenticatedFetch : fetch;
  return fetchFn(url, options)
    .then(checkStatus)
    .then(processResponse)
    .catch(processError({ endpoint, query, auth, url, method }));
}

// These are the functions that are exported for use:
export { makeApiUrl };
export const get = curry(execute)('GET');
export const post = curry(execute)('POST');
export const patch = curry(execute)('PATCH');
export const put = curry(execute)('PUT');
// Any other functions exported above are only exported for test purposes.



// WEBPACK FOOTER //
// ./utils/fetch.js